import {
	PersonAvatar,
	PersonAvatarStatusKind,
	TagList,
	TagModel
} from "client/Components";
import { setTrackingId } from "client/Misc/utils";
import { ReactElement, useMemo } from "react";
import { ContactPresenceStatus } from "client/Api/Email/contacts";
import styles from "./PersonCard.module.less";
import { CommunicationButtons } from "../CommunicationButtons/CommunicationButtons";
import { TagViewModel } from "client/Api/Tags/tags";
import classNames from "classnames";

export type ContactCardVariant = "list" | "details";

export const presenceToAvatarStatusMap: { [key in ContactPresenceStatus]: PersonAvatarStatusKind | undefined; } = {
	[ContactPresenceStatus.NotApplicable]: undefined,
	[ContactPresenceStatus.Available]: PersonAvatarStatusKind.Available,
	[ContactPresenceStatus.Busy]: PersonAvatarStatusKind.Busy,
	[ContactPresenceStatus.DoNotDisturb]: PersonAvatarStatusKind.DoNotDisturb,
	[ContactPresenceStatus.Away]: PersonAvatarStatusKind.Away,
	[ContactPresenceStatus.BeRightBack]: PersonAvatarStatusKind.BeRightBack,
	[ContactPresenceStatus.Offline]: PersonAvatarStatusKind.Offline,
	[ContactPresenceStatus.OutOfOffice]: PersonAvatarStatusKind.OutOfOffice,
	[ContactPresenceStatus.Unknown]: undefined,
};

export type ContactCardProps = {
	contactId: string;
	displayName: string | null;
	photoUrl: string | null;
	initials: string | null;
	jobTitle: string | null;
	department: string | null;
	officeLocation: string | null;
	company?: string | null;
	presence: ContactPresenceStatus;
	phoneNumber: string | null;
	emailAddress: string | null;
	onNavigate?(): void;
	skills: readonly TagViewModel[] | null;
	isActive: boolean;
};

export const PersonCard = (props: ContactCardProps): ReactElement => {
	const {
		contactId,
		jobTitle,
		company,
		department,
		officeLocation,
		displayName,
		initials,
		photoUrl,
		presence,
		emailAddress,
		phoneNumber,
		onNavigate,
		skills,
		isActive
	} = props;

	const jobInfo: string = [
		jobTitle,
		department,
		officeLocation,
		company
	]
		.filter(Boolean)
		.join(" | ");

	const skillsToRender = useMemo(
		(): readonly TagModel<number>[] => {
			if (!skills) {
				return [];
			}

			return skills.map(
				x => ({
					text: x.name,
					value: x.id,
					color: "secondary"
				})
			);
		},
		[skills]
	);

	return (
		<div
			{...setTrackingId(`contact-card-item|${contactId}`)}
			className={classNames(
				styles.root,
				{ [styles.root_active]: isActive }
			)}
			onClick={onNavigate}
		>
			<section
				{...setTrackingId(`contact-info|${contactId}`)}
				className={styles.content}
			>
				<div className={styles.avatar}>
					<PersonAvatar
						initials={initials ?? ""}
						imageSrc={photoUrl}
						size="xx-large"
						trackingId="avatar"
						status={{
							kind: "status",
							status: presenceToAvatarStatusMap[presence]
						}}
					/>
				</div>
				<h4
					{...setTrackingId("name")}
					className={styles.name}
				>
					{displayName}
				</h4>
				<div
					{...setTrackingId("job-info")}
					className={styles.info}
				>
					{jobInfo}
				</div>
				{skillsToRender.length > 0 && (
					<div className={styles.tags}>
						<TagList
							trackingId="skill-list"
							tags={skillsToRender}
							shouldBubbleTagClick={false}
							alignment="center"
							showMoreTagsColor="secondary"
						/>
					</div>
				)}
			</section>
			<footer className={styles.footer}>
				<CommunicationButtons
					emailAddress={emailAddress}
					phoneNumber={phoneNumber}
					variant="list-item"
				/>
			</footer>
		</div>
	);
};
PersonCard.displayName = "ContactCard";